import React, { useState, useEffect, useMemo } from 'react';
import CustomDropdown from 'components/v2/dropdowns/CustomDropdown';
import RadioList from 'components/v2/RadioList';
import FormGroup from 'components/v2/FormGroup';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import { Button } from 'react-bootstrap';
import { titleize } from 'underscore.string';
import Spinner from 'components/ui/Spinner';
import { getYards } from 'utils/YardAPIUtils';
import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import LoadingCircle from 'components/ui/LoadingCircle';
import { isString, map, uniqBy } from 'lodash';
import Alert from 'components/ui/Alert';

const Loading = function () {
  return (
    <div
      data-testid="bulkAddYardsLoader"
      className="justify-center w-full flex flex-col"
    >
      <div className="align-middle">
        <span className="text-5xl text-green">
          <Spinner showMessage={false} />
        </span>
      </div>
    </div>
  );
};

const YardStopForm = ({ shipment, onClose }) => {
  const {
    id: shipmentId,
    market: { market_id: marketId },
  } = shipment;
  const { t } = useTranslation();
  const stopIntentOptions = useMemo(
    () => [
      {
        id: 'deliver_full_pickup_full',
        name: t('DELIVERY_FULL_PICKUP_FULL'),
      },
      {
        id: 'deliver_empty_pickup_empty',
        name: t('DELIVERY_EMPTY_PICKUP_EMPTY'),
      },
    ],
    [],
  );
  const [uiData, setUiData] = useState({
    intent: null,
    yardId: null,
    equipments: null,
    stop1_start: '',
    stop1_end: '',
    stop2_start: '',
    stop2_end: '',
  });
  const [isValid, setIsValid] = useState(false);
  const [yards, setYards] = useState(null);
  const [loading, setLoading] = useState(false);
  const stopsEquipments = map(shipment.stops, 'equipments');
  const hasEquipment = stopsEquipments.some((equipment) =>
    Boolean(equipment.length),
  );

  const equipments = uniqBy(
    stopsEquipments.filter((arr) => arr.length),
    'equipment_id',
  ).map((equipment) => {
    if (hasEquipment) {
      const equipmentId = equipment[0].equipment_id;
      return {
        id: equipmentId,
        name: equipmentId,
      };
    }

    return {
      id: 0,
      name: t('NO_EQUIPMENT_IN_CURRENT_SHIPMENT'),
    };
  });

  const setDateToDatePicker = (type) => {
    return uiData[type] ? moment(uiData[type]).format('MM/DD/YY') : '';
  };

  const preparedYards = useMemo(() => {
    if (!yards) {
      return;
    }
    return yards.map((yard) => {
      return {
        id: yard.id,
        name: yard.nameInternal,
      };
    });
  }, [yards]);

  const preparePayload = () => {
    let stops = [
      {
        off_site: true,
        shipment: shipmentId,
      },
      {
        off_site: true,
        shipment: shipmentId,
      },
    ];
    const updateStops = (newData) => {
      stops = [
        {
          ...stops[0],
          ...newData,
        },
        {
          ...stops[1],
          ...newData,
        },
      ];
    };

    Object.keys(uiData).forEach((key) => {
      const isStartField = key.includes('start');
      const isEndField = key.includes('end');
      const value = uiData[key];

      if (isStartField || isEndField) {
        const stopNumber = key.replace(/\D/g, '');
        const changeItemNumber = stopNumber - 1;
        const restItemNumber = 2 - stopNumber; // get 0 or 1

        stops[changeItemNumber] = {
          ...stops[changeItemNumber],
          [`window_${isStartField ? 'start' : 'end'}`]: value,
        };
        stops[restItemNumber] = {
          ...stops[restItemNumber],
        };
      } else if (key === 'intent') {
        const empty = value === 'deliver_empty_pickup_empty';

        stops = [
          {
            ...stops[0],
            action: 'deliver',
            empty,
          },
          {
            ...stops[1],
            action: 'pickup',
            empty,
          },
        ];
      } else if (key === 'yardId') {
        const { contactName: name, address } = yards.find(
          (yard) => yard.id === value,
        );

        const yardRelatedFields = {
          yardId: value,
          location: {
            name,
            address,
          },
        };

        updateStops(yardRelatedFields);
      } else if (key === 'equipments') {
        const equipments = [value];

        updateStops({ equipments });
      }
    });

    return {
      jobId: shipmentId,
      stops,
    };
  };

  const updateModel = (field, value) => {
    setUiData((data) => {
      return {
        ...data,
        [field]: value,
      };
    });
  };

  const validateData = () =>
    Object.values(uiData).every((item) => Boolean(item));

  const handleSave = async () => {
    setLoading(true);
    const response = await ShipmentWebAPIUtils.addYardStop([preparePayload()]);
    setLoading(false);
    if (response.success) {
      onClose();
    }
  };

  const showPortProWarning = () => {
    const { carrier } = shipment;

    return carrier?.portpro?.enabled;
  };

  useEffect(() => {
    const fetchYards = async (
      sortBy,
      sortOrder,
      skip,
      limit,
      yardInput = {
        id: null,
        marketId: null,
        nameInternal: null,
        status: null,
      },
    ) => {
      return getYards(sortBy, sortOrder, skip, limit, yardInput);
    };
    async function receiveYards() {
      const yardData = await fetchYards('nameInternal', 'ascend', null, null, {
        id: null,
        marketId,
        nameInternal: null,
        status: 'active',
      });

      setYards(yardData.data.getYards.yards);
    }

    receiveYards();
  }, []);

  useEffect(() => {
    setIsValid(validateData());
  }, [uiData]);

  if (yards) {
    return (
      <div>
        {showPortProWarning() && (
          <div className="mb-2">
            <Alert
              className="mt-2"
              type="warning"
              title={t('ADD_STOP_PORTPRO_WARNING')}
            />
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <FormGroup label={t('YARD')}>
            <div data-testid="addYardStopYard">
              <CustomDropdown
                options={preparedYards}
                value={preparedYards.find(
                  (prepared) => prepared.id === uiData.yardId,
                )}
                nameKey="name"
                valueKey="id"
                onChange={(yard) => updateModel('yardId', yard.id)}
              />
            </div>
          </FormGroup>
          <div className="col-span-full">
            <FormGroup label={t('STOP_INTENT')}>
              <div data-testid="addYardStopIntent">
                <RadioList
                  options={stopIntentOptions}
                  selected={stopIntentOptions.find(
                    (intent) => intent.id === uiData.intent,
                  )}
                  onSelect={(intent) => updateModel('intent', intent.id)}
                />
              </div>
            </FormGroup>
          </div>
          <div className="col-span-full">
            <FormGroup label={t('CONTAINER')}>
              <div data-testid="addYardStopEquipment">
                <RadioList
                  label={t('SELECT')}
                  options={equipments}
                  disabled={!hasEquipment}
                  selected={equipments.find(
                    (equipment) => equipment.id === uiData.equipments,
                  )}
                  onSelect={(equipments) =>
                    updateModel('equipments', equipments.id)
                  }
                />
              </div>
            </FormGroup>
          </div>
          <FormGroup label={t('STOP_1_START')}>
            <div data-testid="addYardStopStop1_Start">
              <DatePicker
                placeholderText={t('START')}
                value={setDateToDatePicker('stop1_start')}
                onChange={(value) =>
                  updateModel('stop1_start', moment(value).format())
                }
                className="form-field"
                dropdownMode="select"
                maxDate={
                  uiData.stop1_end && isString(uiData.stop1_end)
                    ? moment(uiData.stop1_end)
                    : null
                }
              />
            </div>
          </FormGroup>
          <FormGroup label={t('STOP_1_END')}>
            <div data-testid="addYardStopStop1_End">
              <DatePicker
                placeholderText={t('END')}
                value={setDateToDatePicker('stop1_end')}
                onChange={(value) =>
                  updateModel('stop1_end', moment(value).format())
                }
                className="form-field"
                dropdownMode="select"
                minDate={
                  uiData.stop1_start && isString(uiData.stop1_start)
                    ? moment(uiData.stop1_start)
                    : null
                }
              />
            </div>
          </FormGroup>
          <FormGroup label={t('STOP_2_START')}>
            <div data-testid="addYardStopStop2_Start">
              <DatePicker
                dateFormat="YYYY-MM-DD"
                placeholderText={t('START')}
                value={setDateToDatePicker('stop2_start')}
                onChange={(value) =>
                  updateModel('stop2_start', moment(value).format())
                }
                className="form-field"
                dropdownMode="select"
                maxDate={
                  uiData.stop2_end && isString(uiData.stop2_end)
                    ? moment(uiData.stop2_end)
                    : null
                }
              />
            </div>
          </FormGroup>
          <FormGroup label={t('STOP_2_END')}>
            <div data-testid="addYardStopStop2_End">
              <DatePicker
                dateFormat="YYYY-MM-DD"
                placeholderText={t('END')}
                value={setDateToDatePicker('stop2_end')}
                onChange={(value) =>
                  updateModel('stop2_end', moment(value).format())
                }
                className="form-field"
                dropdownMode="select"
                minDate={
                  uiData.stop2_start && isString(uiData.stop2_start)
                    ? moment(uiData.stop2_start)
                    : null
                }
              />
            </div>
          </FormGroup>
        </div>

        <div
          data-testid="addYardStopControls"
          className="col-span-full flex justify-end gap-3 mt-4"
        >
          <Button data-testid="addYardStopCancel" onClick={onClose}>
            {t('CANCEL')}
          </Button>
          <Button
            data-testid="addYardStopAddStop"
            disabled={!isValid || loading}
            bsStyle="primary"
            onClick={handleSave}
          >
            {loading ? (
              <LoadingCircle text={t('LOADING')} />
            ) : (
              titleize(t('ADD_STOPS'))
            )}
          </Button>
        </div>
      </div>
    );
  }

  return <Loading />;
};

export default YardStopForm;
